'use client';

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

export type PlatformType =
  | 'Windows'
  | 'MacOS'
  | 'Linux'
  | 'iOS'
  | 'Android'
  | 'Unknown';

interface PlatformInfo {
  platform: PlatformType;
  isMobile: boolean;
  isDesktop: boolean;
  isWebkit: boolean;
  userAgent: string;
}

/**
 * Hook to detect the user's operating system platform
 * Analyzes the navigator.userAgent to determine if the user is on Windows, Linux, iOS, etc.
 * Also provides additional context like whether it's a mobile or desktop platform
 */
export const usePlatform = (): PlatformInfo => {
  const [platformInfo, setPlatformInfo] = useState<PlatformInfo>({
    platform: 'Unknown',
    isMobile: false,
    isDesktop: false,
    isWebkit: false,
    userAgent: '',
  });

  useEffect(() => {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return;
    }

    const userAgent = navigator.userAgent.toLowerCase();
    // const vendor = navigator.vendor.toLowerCase();
    let platform: PlatformType = 'Unknown';

    // Detect platform based on user agent
    if (userAgent.includes('win')) {
      platform = 'Windows';
    } else if (userAgent.includes('mac')) {
      platform = 'MacOS';
    } else if (userAgent.includes('linux')) {
      platform = 'Linux';
    } else if (
      userAgent.includes('iphone') ||
      userAgent.includes('ipad') ||
      userAgent.includes('ipod')
    ) {
      platform = 'iOS';
    } else if (userAgent.includes('android')) {
      platform = 'Android';
    }

    // Determine if it's mobile or desktop
    const isMobile =
      platform === 'iOS' ||
      platform === 'Android' ||
      /mobi|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        userAgent,
      );

    const isDesktop =
      platform === 'Windows' || platform === 'MacOS' || platform === 'Linux';

    const isWebkitBased =
      // vendor.includes('apple') &&
      userAgent.includes('safari') && !userAgent.includes('chrome'); // This is crucial!

    setPlatformInfo({
      platform,
      isMobile,
      isDesktop,
      isWebkit: isWebkitBased,
      userAgent: navigator.userAgent,
    });
  }, []);

  return platformInfo;
};

/**
 * Simplified hook that returns just the platform name
 * Useful when you only need the platform string
 */
export const usePlatformName = (): PlatformType => {
  const { platform } = usePlatform();
  return platform;
};

/**
 * Hook to check if the user is on a specific platform
 * @param targetPlatform - The platform to check against
 * @returns boolean indicating if the user is on the target platform
 */
export const useIsPlatform = (targetPlatform: PlatformType): boolean => {
  const { platform } = usePlatform();
  return platform === targetPlatform;
};

/**
 * Hook to get platform-specific keyboard shortcuts
 * Returns the appropriate modifier key based on the platform
 */
export const usePlatformShortcuts = () => {
  const { platform } = usePlatform();

  const modifierKey = platform === 'MacOS' ? '⌘' : 'Ctrl';
  const altKey = platform === 'MacOS' ? '⌥' : 'Alt';

  return {
    modifierKey,
    altKey,
    getShortcut: (key: string) => `${modifierKey}+${key}`,
    getAltShortcut: (key: string) => `${altKey}+${key}`,
  };
};

/**
 * Hook for conditional rendering based on platform
 * Useful for showing different UI elements based on the user's platform
 */
export const usePlatformConditional = () => {
  const { platform, isMobile, isDesktop } = usePlatform();

  return {
    // Render content only on specific platforms
    renderOnWindows: (content: ReactNode) =>
      platform === 'Windows' ? content : null,
    renderOnMac: (content: ReactNode) =>
      platform === 'MacOS' ? content : null,
    renderOnLinux: (content: ReactNode) =>
      platform === 'Linux' ? content : null,
    renderOnIOS: (content: ReactNode) => (platform === 'iOS' ? content : null),
    renderOnAndroid: (content: ReactNode) =>
      platform === 'Android' ? content : null,

    // Render content based on device type
    renderOnMobile: (content: ReactNode) => (isMobile ? content : null),
    renderOnDesktop: (content: ReactNode) => (isDesktop ? content : null),

    // Render different content for mobile vs desktop
    renderConditional: (mobileContent: ReactNode, desktopContent: ReactNode) =>
      isMobile ? mobileContent : desktopContent,

    // Platform info for custom conditions
    platform,
    isMobile,
    isDesktop,
  };
};
